/**
 * @file SearchHeader.tsx
 * @description MultipleCascader 面板顶部的搜索框组件
 *
 * 模块职责：
 * - 渲染已选项 Tag + 搜索 Input（与 NewRegionSelect 搜索区一致）
 * - 阻止点击事件冒泡，避免触发 Dropdown 关闭
 * - 将输入变化委托给父组件
 *
 * 使用场景：
 * - 当 searchable=true 时，由 Panel 在顶部渲染本组件。
 */

import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Icon, Input } from 'tea-component';
import { useIntl } from 'umi';

import type { TStringPath } from '../types';

/** 搜索区单个已选 Tag */
export interface ISearchTagItem {
  pathKey: string;
  path: TStringPath;
  label: React.ReactNode;
  disabled?: boolean;
}

interface ISearchHeaderProps {
  /** 搜索框占位文案 */
  placeholder?: string;
  /** 当前搜索关键字（受控） */
  searchKeyword: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 搜索关键字变化 */
  onSearchKeywordChange: (nextValue: string) => void;
  /** 已选项 Tag（来自当前 value 在 data 上的解析） */
  tags: ISearchTagItem[];
  /** 点击单个 Tag 关闭：移除对应路径及子路径选中 */
  onRemoveTag: (path: TStringPath) => void;
  /** 点击右侧 dismiss：清空关键字并清空全部已选（与 NewRegionSelect 一致） */
  onClearBar: () => void;
}

/**
 * 搜索头组件：封装 Tag + 搜索框 UI 与事件冒泡阻止逻辑。
 */
const SearchHeader: React.FC<ISearchHeaderProps> = ({
  placeholder,
  searchKeyword,
  disabled,
  onSearchKeywordChange,
  tags,
  onRemoveTag,
  onClearBar,
}) => {
  const { formatMessage } = useIntl();
  const searchInputRef = useRef<any>(null);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const handleChange = (nextValue: string): void => {
    onSearchKeywordChange(String(nextValue || ''));
  };

  const handleDismissClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    onClearBar();
  };

  const handleClickSearchWrap = (event: React.MouseEvent): void => {
    event.stopPropagation();
    searchInputRef.current?.focus();
  };

  const handleCloseTag = (
    event: React.MouseEvent,
    path: TStringPath,
    tagDisabled?: boolean,
  ): void => {
    event.stopPropagation();
    if (tagDisabled) {
      return;
    }
    onRemoveTag(path);
  };

  const resolvedPlaceholder =
    placeholder ?? formatMessage({ id: 'search' });
  const level3Tags = tags.filter((tag) => tag.path.length >= 3);

  const showDismiss =
    !disabled && (level3Tags.length > 0 || Boolean(searchKeyword));

  return (
    <div
      className="dbl-multiple-cascader-search-container"
      onClick={(event): void => {
        event.stopPropagation();
      }}
    >
      <div className="dbl-multiple-cascader-search-wrap">
        <div className="dbl-multiple-cascader-search-input-wrap">
          <div
            className="dbl-multiple-cascader-search-input-wrap-inner"
            onClick={handleClickSearchWrap}
          >
            {level3Tags.map((tag) => (
              <div
                className={classNames(
                  'tea-tag',
                  'dbl-multiple-cascader-search-tag',
                  {
                    'dbl-multiple-cascader-search-tag--disabled': tag.disabled,
                  },
                )}
                key={tag.pathKey}
              >
                <span>{tag.label}</span>
                <Icon
                  type="close"
                  onClick={(e): void => handleCloseTag(e, tag.path, tag.disabled)}
                />
              </div>
            ))}
            <Input
              placeholder={resolvedPlaceholder}
              value={searchKeyword}
              ref={searchInputRef}
              className="dbl-multiple-cascader-search-input"
              type="text"
              disabled={disabled}
              onChange={(val): void => {
                handleChange(val);
              }}
            />
          </div>
          <div className="dbl-multiple-cascader-search-icon">
            {showDismiss ? (
              <Icon type="dismiss" onClick={handleDismissClick} />
            ) : null}
            <Icon type="search" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SearchHeader);
